import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DownloadIcon, ListChecksIcon, MoreVerticalIcon, SearchIcon, Settings2Icon, TagIcon, UsersIcon } from 'lucide-react';
import { useAuth, useWorkspace } from '../../contexts/AuthContext';
import { bulkUpdateStage, deleteLead, updateLeadStage } from '../../services/leadService';
import { usePreferences } from '../../hooks/usePreferences';
import { DEFAULT_LEADS_PER_PAGE, LEADS_PER_PAGE_OPTIONS } from '../../services/preferencesService';
import { downloadCsv } from '../../utils/csv';
import { formatDate, formatDateTime, fullName } from '../../utils/format';
import { Lead, LeadNote } from '../../types';
import { PageHeader } from '../../components/crm/PageHeader';
import { DemoBadge } from '../../components/crm/DemoBadge';
import { EmptyState } from '../../components/crm/EmptyState';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { Checkbox } from '../../components/ui/Checkbox';
import { Table } from '../../components/ui/Table';
import { Dropdown } from '../../components/ui/Dropdown';
import { SourceBadge } from '../../components/crm/SourceBadge';
import { StageBadge } from '../../components/crm/StageBadge';
import { StageManager } from '../../components/crm/StageManager';
import { SourceManager } from '../../components/crm/SourceManager';
import { ConfirmDialog } from '../../components/crm/ConfirmDialog';
import { noteColor } from '../../components/crm/noteColors';
import { LeadDrawer } from '../../components/crm/LeadDrawer';

/**
 * Paginación en cliente mientras los datos son de demostración.
 *
 * TODO(Supabase): sustituir por una consulta paginada en servidor
 * (`.range(offset, offset + limit - 1)`) que reciba los mismos filtros,
 * búsqueda y orden, para no descargar todos los leads al navegador.
 * `pageSize` y `currentPage` ya son los únicos valores necesarios.
 */

/**
 * Columnas disponibles en la tabla de leads.
 * TODO(Supabase): guardar la selección por usuario en `user_preferences`.
 */
const COLUMN_OPTIONS: {
  key: string;
  label: string;
}[] = [{
  key: 'name',
  label: 'Nombre'
}, {
  key: 'company',
  label: 'Empresa'
}, {
  key: 'email',
  label: 'Email'
}, {
  key: 'phone',
  label: 'Teléfono'
}, {
  key: 'instagram',
  label: 'Instagram'
}, {
  key: 'city',
  label: 'Ciudad'
}, {
  key: 'service',
  label: 'Tipo de servicio'
}, {
  key: 'adBudget',
  label: 'Inversión actual'
}, {
  key: 'budget',
  label: 'Presupuesto disponible'
}, {
  key: 'source',
  label: 'Fuente'
}, {
  key: 'campaign',
  label: 'Campaña'
}, {
  key: 'landing',
  label: 'Landing'
}, {
  key: 'form',
  label: 'Formulario'
}, {
  key: 'stage',
  label: 'Estado'
}, {
  key: 'owner',
  label: 'Comercial'
}, {
  key: 'createdAt',
  label: 'Fecha'
}, {
  key: 'lastContact',
  label: 'Último contacto'
}, {
  key: 'note',
  label: 'Última nota'
}];
const DEFAULT_COLUMNS = ['name', 'company', 'phone', 'email', 'city', 'service', 'budget', 'source', 'stage', 'createdAt', 'note'];
export function Leads() {
  const {
    user,
    hasPermission
  } = useAuth();
  const {
    organizationId,
    leads,
    stages,
    sources,
    forms,
    landings,
    notes,
    isDemo
  } = useWorkspace();
  const members = useWorkspaceMembers();
  const [drawerLead, setDrawerLead] = useState<string | null>(null);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [stagesOpen, setStagesOpen] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const [confirmTrash, setConfirmTrash] = useState<Lead | null>(null);
  const [trashing, setTrashing] = useState(false);
  const [preferences, updatePreferences] = usePreferences();
  const visibleColumns = preferences.leadColumns;
  const setVisibleColumns = (updater: string[] | ((prev: string[]) => string[])) => updatePreferences({
    leadColumns: typeof updater === 'function' ? updater(visibleColumns) : updater
  });
  const authorName = user ? fullName(user.firstName, user.lastName) : 'Sistema';
  const [search, setSearch] = useState('');
  const [stageId, setStageId] = useState('');
  const [formId, setFormId] = useState('');
  const [landingId, setLandingId] = useState('');
  const [campaign, setCampaign] = useState('');
  const [owner, setOwner] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string[]>([]);
  const [bulkStage, setBulkStage] = useState('');
  const [applying, setApplying] = useState(false);
  const campaigns = useMemo(() => [...new Set(leads.map(l => l.origin.campaign).filter(Boolean))], [leads]);
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return leads.filter(lead => {
      if (term && ![lead.firstName, lead.lastName, lead.company, lead.email, lead.phone].join(' ').toLowerCase().includes(term)) return false;
      if (stageId && lead.stageId !== stageId) return false;
      if (formId && lead.origin.formId !== formId) return false;
      if (landingId && lead.origin.landingId !== landingId) return false;
      if (campaign && lead.origin.campaign !== campaign) return false;
      if (owner && lead.assignedTo !== owner) return false;
      if (from && lead.createdAt < from) return false;
      if (to && lead.createdAt > `${to}T23:59:59`) return false;
      return true;
    });
  }, [leads, search, stageId, formId, landingId, campaign, owner, from, to]);
  const pageSize = preferences.leadsPerPage || DEFAULT_LEADS_PER_PAGE;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const offset = (currentPage - 1) * pageSize;
  const paged = filtered.slice(offset, offset + pageSize);
  const rangeFrom = filtered.length === 0 ? 0 : offset + 1;
  const rangeTo = Math.min(offset + pageSize, filtered.length);

  /** Números de página a mostrar, con la actual siempre centrada. */
  const pageNumbers = useMemo(() => {
    const max = 5;
    if (totalPages <= max) return Array.from({
      length: totalPages
    }, (_, i) => i + 1);
    const start = Math.max(1, Math.min(currentPage - 2, totalPages - max + 1));
    return Array.from({
      length: max
    }, (_, i) => start + i);
  }, [currentPage, totalPages]);
  function stageName(lead: Lead) {
    return stages.find(s => s.id === lead.stageId)?.name ?? 'Nuevo';
  }
  function memberName(id?: string) {
    const member = members.find(m => m.userId === id);
    return member ? fullName(member.firstName, member.lastName) : 'Sin asignar';
  }

  /** Última nota del lead, para la vista previa en la tabla. */
  function lastNote(leadId: string) {
    return notes.filter(n => n.leadId === leadId).sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
  }
  function exportCsv() {
    downloadCsv('leads-ava.csv', ['Nombre', 'Apellidos', 'Empresa', 'Email', 'Teléfono', 'Instagram', 'Ciudad', 'País', 'Servicio solicitado', 'Presupuesto publicidad', 'Presupuesto disponible', 'Formulario', 'Landing', 'Fuente', 'Campaña', 'Estado', 'Comercial', 'Fecha', 'Última nota'], filtered.map(lead => [lead.firstName, lead.lastName, lead.company, lead.email, lead.phone, lead.instagram ?? '', lead.city ?? '', lead.country ?? '', lead.service ?? '', lead.adBudget ?? '', lead.budget ?? '', forms.find(f => f.id === lead.origin.formId)?.name ?? '', landings.find(l => l.id === lead.origin.landingId)?.name ?? '', lead.origin.source, lead.origin.campaign, stageName(lead), memberName(lead.assignedTo), formatDate(lead.createdAt), lastNote(lead.id)?.body ?? '']));
  }

  /** Menú de acciones rápidas de cada fila o tarjeta. */
  function rowActions(lead: Lead) {
    const items = [{
      label: 'Abrir lead',
      onSelect: () => setDrawerLead(lead.id)
    }, {
      label: 'Abrir WhatsApp',
      onSelect: () => window.open(`https://wa.me/${lead.phone.replace(/\D/g, '')}`, '_blank', 'noopener')
    }, {
      label: 'Copiar teléfono',
      onSelect: () => navigator.clipboard?.writeText(lead.phone)
    }];
    if (hasPermission('leads.edit')) {
      items.splice(1, 0, {
        label: 'Añadir nota',
        onSelect: () => setDrawerLead(lead.id)
      });
    }
    if (hasPermission('leads.delete')) {
      items.push({
        label: 'Mover a papelera',
        destructive: true,
        onSelect: () => setConfirmTrash(lead)
      } as never);
    }
    return items;
  }
  async function applyBulk() {
    if (!bulkStage || selected.length === 0) return;
    setApplying(true);
    await bulkUpdateStage(selected, bulkStage, user ? fullName(user.firstName, user.lastName) : 'Sistema');
    setApplying(false);
    setSelected([]);
    setBulkStage('');
  }
  if (leads.length === 0) {
    return <>
        <PageHeader title="Leads" description="Contactos recibidos por tu empresa." data-id="element-1089" data-mp="2vcxib6ccs84o" />
        <EmptyState icon={<UsersIcon className="h-5 w-5" data-id="element-1091" data-mp="3ryzhh6vq9tgk" />} title="Todavía no tienes contactos" description="Publica una landing o comparte un formulario para comenzar a recibir leads." action={<Link to="/app/landings" data-id="element-1092" data-mp="3e1kojs69n3ju">
              <Button data-id="element-1093" data-mp="3e0j08m08okk6">Ir a landings</Button>
            </Link>} data-id="element-1090" data-mp="3s0ts9sdac56x" />
      </>;
  }
  return <>
      <PageHeader title="Leads" description={`${filtered.length} contactos que cumplen los filtros.`} actions={<>
            {isDemo && <DemoBadge className="self-center" data-id="element-1095" data-mp="9dr63vfgw01w" />}
            <Button variant="secondary" leftIcon={<DownloadIcon className="h-4 w-4" data-id="element-1097" data-mp="4g8shrqi8wnk" />} onClick={exportCsv} data-id="element-1096" data-mp="9eit4hv00pgk">
              Exportar CSV
            </Button>
            <Button variant="secondary" leftIcon={<Settings2Icon className="h-4 w-4" data-id="element-1099" data-mp="df2obfw0hj02" />} onClick={() => setColumnsOpen(true)} data-id="element-1098" data-mp="i8faljd7xdpn">
              Personalizar columnas
            </Button>
            <Button variant="secondary" leftIcon={<ListChecksIcon className="h-4 w-4" data-id="element-1101" data-mp="n81kpjxr2du8" />} onClick={() => setStagesOpen(true)} data-id="element-1100" data-mp="n6gs3zuzx4z0">
              Gestionar estados
            </Button>
            <Button variant="secondary" leftIcon={<TagIcon className="h-4 w-4" data-id="element-1103" data-mp="32w1owif57nzd" />} onClick={() => setSourcesOpen(true)} data-id="element-1102" data-mp="x4d1am4dhpuh">
              Gestionar fuentes
            </Button>
          </>} data-id="element-1094" data-mp="27n386wjf4xo4" />

      {/* Filtros */}
      <div className="mb-5 rounded-2xl border border-border bg-background p-5 shadow-soft" data-id="element-1104" data-mp="2osch8pq6oj2n">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" data-id="element-1105" data-mp="2srtmbi1p7e7a">
          {/* Buscador: la lupa va absolutamente posicionada y el campo
              reserva su espacio con padding, para que nunca se solapen. */}
          <div className="relative" data-id="element-1106" data-mp="2jz9tiy8383ud">
            <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" data-id="element-1107" data-mp="2jy8b28oqsf2m" />
            <input id="buscar" type="search" aria-label="Buscar leads" placeholder="Buscar por nombre, empresa o email" value={search} onChange={e => {
            setSearch(e.target.value);
            setPage(1);
          }} className="h-11 w-full rounded-xl border border-border bg-background pl-11 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" data-id="element-1108" data-mp="2a0anlnepimb2" />
          </div>
          <Select id="f-estado" placeholder="Todos los estados" options={stages.map(s => ({
          value: s.id,
          label: s.name
        }))} value={stageId} onChange={e => setStageId(e.target.value)} data-id="element-1109" data-mp="3oy6g1dvlujsm" />
          <Select id="f-form" placeholder="Todos los formularios" options={forms.map(f => ({
          value: f.id,
          label: f.name
        }))} value={formId} onChange={e => setFormId(e.target.value)} data-id="element-1110" data-mp="gjxuuqvewvah" />
          <Select id="f-landing" placeholder="Todas las landings" options={landings.map(l => ({
          value: l.id,
          label: l.name
        }))} value={landingId} onChange={e => setLandingId(e.target.value)} data-id="element-1111" data-mp="6richsg7e4p2" />
          <Select id="f-campana" placeholder="Todas las campañas" options={campaigns.map(c => ({
          value: c,
          label: c
        }))} value={campaign} onChange={e => setCampaign(e.target.value)} data-id="element-1112" data-mp="uinuoe9m992j" />
          <Select id="f-comercial" placeholder="Todos los comerciales" options={members.map(m => ({
          value: m.userId,
          label: fullName(m.firstName, m.lastName)
        }))} value={owner} onChange={e => setOwner(e.target.value)} data-id="element-1113" data-mp="1bt1eqv0rm6gm" />
          <Input id="f-desde" type="date" label="Desde" value={from} onChange={e => setFrom(e.target.value)} data-id="element-1114" data-mp="1pr85zsb7l87b" />
          <Input id="f-hasta" type="date" label="Hasta" value={to} onChange={e => setTo(e.target.value)} data-id="element-1115" data-mp="1zim17nw4nlun" />
        </div>
      </div>

      {/* Acciones en lote */}
      {selected.length > 0 && hasPermission('leads.edit') && <div className="mb-4 flex flex-col gap-3 rounded-lg border border-primary/30 bg-primary/5 p-4 sm:flex-row sm:items-center" data-id="element-1116" data-mp="3673zd8xwli1w">
          <p className="text-sm font-medium text-foreground" data-id="element-1117" data-mp="318k9ixg6vm49">
            {selected.length} leads seleccionados
          </p>
          <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:justify-end" data-id="element-1118" data-mp="3f21vsq09fcxz">
            <Select id="bulk" placeholder="Cambiar estado a…" options={stages.map(s => ({
          value: s.id,
          label: s.name
        }))} value={bulkStage} onChange={e => setBulkStage(e.target.value)} data-id="element-1119" data-mp="3b0qb8yw8thwn" />
            <Button onClick={applyBulk} loading={applying} disabled={!bulkStage} data-id="element-1120" data-mp="2waiq2beupks9">
              Aplicar
            </Button>
            <Button variant="ghost" onClick={() => setSelected([])} data-id="element-1121" data-mp="2rcqzbkpy04pn">
              Cancelar
            </Button>
          </div>
        </div>}

      {/* Tabla escritorio */}
      <div className="hidden lg:block" data-id="element-1122" data-mp="6jvulq8sdd06">
        <Table data={paged} rowKey={lead => lead.id} selectable={hasPermission('leads.edit')} selectedKeys={selected} onSelectionChange={setSelected} emptyMessage="Ningún lead cumple estos filtros." columns={[{
        key: 'name',
        header: 'Nombre',
        sortable: true,
        render: lead => <button type="button" onClick={() => setDrawerLead(lead.id)} className="text-left font-medium text-foreground hover:text-primary" data-id="element-1124" data-mp="xrnnbkfm9uw0">
                  {fullName(lead.firstName, lead.lastName)}
                </button>
      }, {
        key: 'company',
        header: 'Empresa',
        render: lead => lead.company
      }, {
        key: 'phone',
        header: 'Teléfono',
        render: lead => lead.phone
      }, {
        key: 'email',
        header: 'Email',
        render: lead => lead.email
      }, {
        key: 'instagram',
        header: 'Instagram',
        render: lead => lead.instagram || '—'
      }, {
        key: 'city',
        header: 'Ciudad',
        render: lead => lead.city || '—'
      }, {
        key: 'service',
        header: 'Servicio solicitado',
        render: lead => lead.service || '—'
      }, {
        key: 'adBudget',
        header: 'Presup. publicidad',
        render: lead => lead.adBudget || '—'
      }, {
        key: 'budget',
        header: 'Presup. disponible',
        render: lead => lead.budget || '—'
      }, {
        key: 'source',
        header: 'Fuente',
        render: lead => <SourceBadge source={lead.origin.source} sources={sources} data-id="element-1125" data-mp="tomsf0a0pob0" />
      }, {
        key: 'stage',
        header: 'Estado',
        render: lead => <StageBadge stage={stages.find(s => s.id === lead.stageId)} stages={hasPermission('leads.edit') ? stages : undefined} onChange={id => updateLeadStage(lead.id, id, authorName)} data-id="element-1126" data-mp="30d6qog3jbfck" />
      }, {
        key: 'owner',
        header: 'Comercial',
        render: lead => memberName(lead.assignedTo)
      }, {
        key: 'campaign',
        header: 'Campaña',
        render: lead => lead.origin.campaign || '—'
      }, {
        key: 'landing',
        header: 'Landing',
        render: lead => landings.find(l => l.id === lead.origin.landingId)?.name ?? '—'
      }, {
        key: 'form',
        header: 'Formulario',
        render: lead => forms.find(f => f.id === lead.origin.formId)?.name ?? '—'
      }, {
        key: 'createdAt',
        header: 'Fecha',
        sortable: true,
        render: lead => formatDate(lead.createdAt)
      }, {
        key: 'lastContact',
        header: 'Último contacto',
        render: lead => lead.lastContactAt ? formatDate(lead.lastContactAt) : 'Sin contactar'
      }, {
        key: 'note',
        header: 'Última nota',
        render: lead => <NotePreview note={lastNote(lead.id)} data-id="element-1127" data-mp="1a8nfhf695htp" />
      }, {
        key: 'actions',
        header: '',
        render: lead => <Dropdown align="right" trigger={<span className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-surface" data-id="element-1129" data-mp="1xyhp7nv9yq9q">
                      <MoreVerticalIcon className="h-4 w-4" aria-hidden="true" data-id="element-1130" data-mp="1t1ix1av68te6" />
                    </span>} items={rowActions(lead)} data-id="element-1128" data-mp="1jyzvcu34gmti" />
      }].filter(column => column.key === 'actions' || visibleColumns.includes(column.key))} data-id="element-1123" data-mp="f1fqtl6fa2os" />
      </div>

      {/* Tarjetas móvil */}
      <ul className="grid gap-3 lg:hidden" data-id="element-1131" data-mp="27lbzdqleglx6">
        {paged.map(lead => {
        const note = lastNote(lead.id);
        const expanded = expandedCard === lead.id;
        return <li key={lead.id} className="rounded-2xl border border-border bg-background p-5 shadow-soft" data-id="element-1132" data-mp="1ymqhzyrnzcxz">
              <div className="flex items-start justify-between gap-3" data-id="element-1133" data-mp="1ow59njm6uu1v">
                <div className="min-w-0" data-id="element-1134" data-mp="1owf2deymbfb9">
                  <p className="truncate font-semibold text-foreground" data-id="element-1135" data-mp="1awmgbwy1dn9m">
                    {fullName(lead.firstName, lead.lastName)}
                  </p>
                  <p className="truncate text-sm text-muted-foreground" data-id="element-1136" data-mp="1funvi6scd2ft">
                    {lead.company}
                  </p>
                </div>
                <Dropdown align="right" trigger={<span className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground" data-id="element-1138" data-mp="1525mmloi8zjg">
                      <MoreVerticalIcon className="h-4 w-4" aria-hidden="true" data-id="element-1139" data-mp="1a0781d9elay3" />
                    </span>} items={rowActions(lead)} data-id="element-1137" data-mp="1j26oo4giad0i" />
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2" data-id="element-1140" data-mp="21r3iy8bwl5iq">
                <StageBadge stage={stages.find(s => s.id === lead.stageId)} stages={hasPermission('leads.edit') ? stages : undefined} onChange={id => updateLeadStage(lead.id, id, authorName)} data-id="element-1141" data-mp="1o104w4n6gi4d" />
                <SourceBadge source={lead.origin.source} sources={sources} data-id="element-1142" data-mp="2gncfv38bn6rz" />
              </div>

              {note && <div className={`mt-3 rounded-lg border-l-4 px-3 py-2 ${noteColor(note.color).bg} ${noteColor(note.color).border}`} data-id="element-1143" data-mp="2uupgntytcw2w">
                  <p className="line-clamp-2 text-xs leading-snug text-foreground" data-id="element-1144" data-mp="2qr9nj7lydduy">
                    {note.body}
                  </p>
                </div>}

              {expanded && <dl className="mt-3 grid grid-cols-2 gap-2 border-t border-border pt-3 text-xs text-muted-foreground" data-id="element-1145" data-mp="2znqx46eo53ol">
                  <div className="truncate" data-id="element-1146" data-mp="27urmfzl7s1m1">{lead.email}</div>
                  <div className="truncate" data-id="element-1147" data-mp="27wb3vtilwipe">{lead.phone}</div>
                  <div className="truncate" data-id="element-1148" data-mp="2luj5ieql886w">{lead.city || '—'}</div>
                  <div className="truncate" data-id="element-1149" data-mp="2gv6bna1au0ut">{lead.service || '—'}</div>
                  <div className="truncate" data-id="element-1150" data-mp="2gtv6p6w4an24">{lead.budget || '—'}</div>
                  <div className="truncate" data-id="element-1151" data-mp="12z4kk2vmo8j">{formatDate(lead.createdAt)}</div>
                </dl>}

              <div className="mt-3 flex gap-2" data-id="element-1152" data-mp="3s9tktp65jza7">
                <Button variant="ghost" size="sm" className="min-h-[44px] flex-1" onClick={() => setExpandedCard(expanded ? null : lead.id)} data-id="element-1153" data-mp="45wye427822c">
                  {expanded ? 'Ver menos' : 'Ver más'}
                </Button>
                <Button variant="secondary" size="sm" className="min-h-[44px] flex-1" onClick={() => setDrawerLead(lead.id)} data-id="element-1154" data-mp="i5xy3g6q93g8">
                  Abrir ficha
                </Button>
              </div>
            </li>;
      })}
      </ul>

      {/* Paginación */}
      <nav className="mt-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between" aria-label="Paginación de leads" data-id="element-1155" data-mp="3qj4yv840kt3z">
        <p className="order-2 text-sm text-muted-foreground lg:order-1" data-id="element-1156" data-mp="2xvivo0yp7qhn">
          <span className="hidden sm:inline" data-id="element-1157" data-mp="32vdmy5mdyi38">
            Mostrando {rangeFrom}–{rangeTo} de {filtered.length} contactos
          </span>
          <span className="sm:hidden" data-id="element-1158" data-mp="2vatayfw67hon">
            {rangeFrom}–{rangeTo} de {filtered.length}
          </span>
        </p>

        <div className="order-3 flex flex-wrap items-center justify-center gap-2 lg:order-2" data-id="element-1159" data-mp="2qdt5a638ggy2">
          <Button variant="secondary" size="sm" className="min-h-[44px]" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)} data-id="element-1160" data-mp="2lc3r4hkprn5r">
            Anterior
          </Button>
          {totalPages > 1 && <ul className="hidden items-center gap-1 sm:flex" data-id="element-1161" data-mp="27kyyw0jodx5g">
              {pageNumbers.map(number => <li key={number} data-id="element-1162" data-mp="22nqcfnrhv6ye">
                  <button type="button" aria-label={`Ir a la página ${number}`} aria-current={number === currentPage ? 'page' : undefined} onClick={() => setPage(number)} className={`min-h-[44px] min-w-[44px] rounded-xl px-3 text-sm font-medium transition-colors ${number === currentPage ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-surface hover:text-foreground'}`} data-id="element-1163" data-mp="1xom4f0gb3f9n">
                    {number}
                  </button>
                </li>)}
            </ul>}
          <span className="text-sm text-muted-foreground sm:hidden" data-id="element-1164" data-mp="1unkjuujcdy02">
            {currentPage} / {totalPages}
          </span>
          <Button variant="secondary" size="sm" className="min-h-[44px]" disabled={currentPage === totalPages} onClick={() => setPage(currentPage + 1)} data-id="element-1165" data-mp="1zoi2t9c6ew2m">
            Siguiente
          </Button>
        </div>

        {/* Contactos por página */}
        <div className="order-1 flex items-center gap-2 lg:order-3" data-id="element-1166" data-mp="3fz2i71ykwkh2">
          <label htmlFor="leads-per-page" className="whitespace-nowrap text-sm text-muted-foreground" data-id="element-1167" data-mp="3k1h4jw1gi2d3">
            Contactos por página
          </label>
          <select id="leads-per-page" value={pageSize} onChange={e => {
          updatePreferences({
            leadsPerPage: Number(e.target.value)
          });
          setPage(1);
        }} className="h-11 rounded-xl border border-border bg-background px-3 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" data-id="element-1168" data-mp="14euy9gpsgier">
            {LEADS_PER_PAGE_OPTIONS.map(option => <option key={option} value={option} data-id="element-1169" data-mp="213ubzb956cec">
                {option}
              </option>)}
          </select>
        </div>
      </nav>

      <LeadDrawer leadId={drawerLead} onClose={() => setDrawerLead(null)} data-id="element-1170" data-mp="1w5some4onqx2" />

      <StageManager open={stagesOpen} onClose={() => setStagesOpen(false)} organizationId={organizationId} stages={stages} data-id="element-1171" data-mp="6qummqeym3tv" />
      <SourceManager open={sourcesOpen} onClose={() => setSourcesOpen(false)} organizationId={organizationId} sources={sources} data-id="element-1172" data-mp="21thky91psy64" />

      <ConfirmDialog open={Boolean(confirmTrash)} title="Mover a la papelera" description="El lead saldrá de Leads y del embudo, pero conservará sus notas, respuestas e historial. Podrás restaurarlo desde la Papelera." confirmLabel="Mover a la papelera" loading={trashing} onCancel={() => setConfirmTrash(null)} onConfirm={async () => {
      if (!confirmTrash) return;
      setTrashing(true);
      await deleteLead(confirmTrash.id, authorName);
      setTrashing(false);
      setConfirmTrash(null);
      if (drawerLead === confirmTrash.id) setDrawerLead(null);
    }} data-id="element-1173" data-mp="26uf34lmq5s7m" />

      <Modal open={columnsOpen} onClose={() => setColumnsOpen(false)} title="Personalizar columnas" footer={<>
            <Button variant="ghost" onClick={() => setVisibleColumns(COLUMN_OPTIONS.map(c => c.key))} data-id="element-1175" data-mp="wub5py1rpwyh">
              Mostrar todas
            </Button>
            <Button onClick={() => setColumnsOpen(false)} data-id="element-1176" data-mp="ntmvlefhwgqv">Listo</Button>
          </>} data-id="element-1174" data-mp="33jmre1eqaff8">
        <p className="mb-4 text-sm text-muted-foreground" data-id="element-1177" data-mp="iujpdqns44hb">
          Elige la información que quieres ver en la tabla. La tabla se adapta
          automáticamente al número de columnas visibles.
        </p>
        <ul className="grid gap-2 sm:grid-cols-2" data-id="element-1178" data-mp="1bjh79so2vz9j">
          {COLUMN_OPTIONS.map(column => <li key={column.key} data-id="element-1179" data-mp="16s8vipza01t7">
              <Checkbox id={`col-${column.key}`} label={column.label} checked={visibleColumns.includes(column.key)} onChange={e => setVisibleColumns(prev => e.target.checked ? [...prev, column.key] : prev.filter(k => k !== column.key))} data-id="element-1180" data-mp="16rh1vkp8oe5k" />
            </li>)}
        </ul>
      </Modal>
    </>;
}

/**
 * Vista previa de la nota más reciente: dos líneas como máximo y la nota
 * completa al pasar el cursor o al pulsar en móvil.
 */
function NotePreview({
  note
}: {
  note?: LeadNote;
}) {
  const [open, setOpen] = useState(false);
  if (!note) return <span className="text-muted-foreground" data-id="element-1181" data-mp="1ll005nu3531d">Sin notas</span>;
  const style = noteColor(note.color);
  const preview = note.body.length > 90 ? `${note.body.slice(0, 90).trim()}…` : note.body;
  return <span className="relative block w-56" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} data-id="element-1182" data-mp="1ckbm4t4oz2oh">
      <button type="button" onClick={e => {
      e.stopPropagation();
      setOpen(v => !v);
    }} className={`flex w-full items-start gap-2 rounded-lg border-l-4 px-2.5 py-2 text-left ${style.bg} ${style.border}`} data-id="element-1183" data-mp="1gkvl828tsgd9">
        <span className={`mt-1 h-2 w-2 flex-none rounded-full ${style.dot}`} aria-hidden="true" data-id="element-1184" data-mp="3tscxufex45tc" />
        <span className="min-w-0" data-id="element-1185" data-mp="3oubj26oiysno">
          {note.title && <span className="block truncate text-xs font-semibold text-foreground" data-id="element-1186" data-mp="7lenxg7tayae">
              {note.title}
            </span>}
          <span className="line-clamp-2 text-xs leading-snug text-foreground/80" data-id="element-1187" data-mp="2nd8r6dibj47">
            {preview}
          </span>
        </span>
      </button>
      {open && <span className={`absolute left-0 top-full z-30 mt-2 block w-72 rounded-xl border border-border border-l-4 p-3 text-xs leading-relaxed text-foreground shadow-lg ${style.bg} ${style.border}`} data-id="element-1188" data-mp="363akhjrbgz4n">
          <span className="mb-1 block text-[11px] font-medium text-muted-foreground" data-id="element-1189" data-mp="3g0yci1xjfsj4">
            {note.authorName} · {formatDateTime(note.createdAt)}
          </span>
          {note.body}
        </span>}
    </span>;
}
function useWorkspaceMembers() {
  const {
    members
  } = useAuth();
  return members;
}